import React from 'react';
import { useRouteMatch, Link, generatePath, Route, Switch } from 'react-router-dom';
import { Box, Typography } from '@mui/material';
import routes from '../routes';
import RescueReport from './RescueReportView';
import ReceiverReport from './ReceiverReportView';
import RescuedFoodReport from './RescuedFoodReportView';
import GrowthReport from './GrowthReportView';
import VolumeReport from './VolumeReportView';
import QuicksightReports from './QuicksightReportsView';
import SCDashboardAnalytics from '../pages/SiteCoordinatorDashboard/SCDashboardDesktop/SCDashboardAnalytics';
import { Roles } from '../models/roles';
import { currentlyLoggedInOrImpersonatingUserHasAnyRoleInCurrentlySelectedSite } from '../services/auth';
import { Colors } from '../assets/theme/Colors';
import useHasActiveFeatureFlag from '../hooks/useFeatureFlags';
import { FF_ADVANCED_ANALYTICS } from '../components/FeatureFlagWrapper';

const linkSx = {
  textDecoration: 'none',
  color: Colors.gray,
  paddingLeft: '10px',
  '&.active': {
    color: 'primary.main',
    position: 'relative',
    '&:before': {
      content: '""',
      position: 'absolute',
      left: -3,
      top: 0,
      height: '100%',
      borderLeft: '4px solid',
      borderColor: 'primary.main',
    },
  },
};

const ReportsView = () => {
  const { url } = useRouteMatch();
  const hasActiveAnalyticsFF = useHasActiveFeatureFlag(FF_ADVANCED_ANALYTICS);

  const reportsList = [
    {
      name: 'Rescue',
      component: RescueReport,
      roles: [Roles.Admin, Roles.NationalSiteDirector, Roles.SiteDirector, Roles.SiteCoordinator],
      path: generatePath(routes.reports, { tab: 'rescue' }),
      tab: 'rescue',
    },
    {
      name: 'Receiving Agency',
      component: ReceiverReport,
      roles: [
        Roles.Admin,
        Roles.NationalSiteDirector,
        Roles.SiteDirector,
        Roles.SiteCoordinator,
        Roles.DonorAdmin,
        Roles.DonorStaff,
        Roles.ReceiverLocationAdmin,
      ],
      path: generatePath(routes.reports, { tab: 'receiving-agency' }),
      tab: 'receiving-agency',
    },
    {
      name: 'Rescued Food',
      component: RescuedFoodReport,
      roles: [Roles.Admin, Roles.NationalSiteDirector, Roles.SiteDirector, Roles.SiteCoordinator],
      path: generatePath(routes.reports, { tab: 'rescued-food' }),
      tab: 'rescued-food',
    },
    {
      name: 'Growth',
      component: GrowthReport,
      roles: [Roles.Admin, Roles.NationalSiteDirector, Roles.SiteDirector, Roles.SiteCoordinator],
      path: generatePath(routes.reports, { tab: 'growth' }),
      tab: 'growth',
    },
    {
      name: 'Volume',
      component: VolumeReport,
      roles: [Roles.Admin, Roles.NationalSiteDirector, Roles.SiteDirector, Roles.SiteCoordinator],
      path: generatePath(routes.reports, { tab: 'volume' }),
      tab: 'volume',
    },
    {
      name: 'Quicksight Dashboard',
      component: QuicksightReports,
      roles: [Roles.Admin, Roles.NationalSiteDirector],
      path: generatePath(routes.reports, { tab: 'quicksight-dashboard' }),
      tab: 'quicksight-dashboard',
    },
  ];

  if (hasActiveAnalyticsFF) {
    reportsList.push({
      name: 'Advanced Analytics',
      component: SCDashboardAnalytics,
      roles: [Roles.Admin, Roles.NationalSiteDirector, Roles.SiteDirector, Roles.SiteCoordinator],
      path: generatePath(routes.reports, { tab: 'advanced-analytics' }),
      tab: 'advanced-analytics',
    });
  }

  const reports = reportsList.filter((report) =>
    currentlyLoggedInOrImpersonatingUserHasAnyRoleInCurrentlySelectedSite(report.roles)
  );
  const paths = reports.map((report) => report.path);

  const getActiveTab = () => {
    const path = paths.indexOf(url);

    return Math.max(path, 0);
  };

  return (
    <>
      <Typography variant="h5">Reports</Typography>
      <Box mt={5} display="flex" gap="16px">
        <Box minWidth="max-content">
          {reports.map((report, index) => (
            <Typography variant="subtitle2" key={report.name} gutterBottom>
              <Box
                component={Link}
                sx={linkSx}
                className={index !== getActiveTab() ? '' : 'active'}
                to={report.path}
                key={report.name}
              >
                {report.name}
              </Box>
            </Typography>
          ))}
        </Box>
        <Box sx={{ width: 'calc(100% - 170px)' }}>
          <Switch>
            {reports.map((report, index) => (
              <Route
                exact
                path={index === 0 ? [generatePath(routes.reports), report.path] : report.path}
                component={report.component}
                key={report.name}
              />
            ))}
          </Switch>
        </Box>
      </Box>
    </>
  );
};

export default ReportsView;
